import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../config/api';
import { getSessionToken } from '../utils/authSession';

const NotificationContext = createContext();

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5 chime
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.6);
  } catch (e) {}
};

// Helper for local read IDs set
const getReadIdsFromStorage = () => {
  try {
    const stored = localStorage.getItem('waseeny_read_notification_ids');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch (e) {
    return new Set();
  }
};

const saveReadIdsToStorage = (readIdsSet) => {
  try {
    localStorage.setItem('waseeny_read_notification_ids', JSON.stringify(Array.from(readIdsSet)));
  } catch (e) {}
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState(() => {
    try {
      const stored = localStorage.getItem('waseeny_user_notifications');
      const readIds = getReadIdsFromStorage();
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return parsed.map(n => readIds.has(n.id || n._id) ? { ...n, read: true } : n);
    } catch (e) {
      return [];
    }
  });

  const [unreadCount, setUnreadCount] = useState(() => {
    try {
      const stored = localStorage.getItem('waseeny_user_notifications');
      const readIds = getReadIdsFromStorage();
      if (!stored) return 0;
      const parsed = JSON.parse(stored);
      return parsed.filter(n => !n.read && !readIds.has(n.id || n._id)).length;
    } catch (e) {
      return 0;
    }
  });

  const [loading, setLoading] = useState(false);
  const [toastNotification, setToastNotification] = useState(null);
  const [socket, setSocket] = useState(null);

  const getAuthToken = () => getSessionToken();

  // Safely decode user ID from localStorage or JWT Token
  const getUserId = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?._id || user?.id) return (user._id || user.id).toString();
      }
    } catch (e) {}

    const token = getAuthToken();
    if (!token) return null;

    try {
      const payloadBase64 = token.split('.')[1];
      if (!payloadBase64) return null;
      const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const parsed = JSON.parse(jsonPayload);
      return (parsed._id || parsed.id || parsed.userId || parsed.sub || null)?.toString();
    } catch (e) {
      return null;
    }
  };

  // Helper to save notifications to localStorage
  const saveNotificationsToStorage = (notifList) => {
    try {
      localStorage.setItem('waseeny_user_notifications', JSON.stringify(notifList.slice(0, 50)));
    } catch (e) {}
  };

  // Trigger floating Toast Notification on screen
  const showToast = useCallback((notif) => {
    if (!notif) return;
    setToastNotification(notif);
    playNotificationSound();

    setTimeout(() => {
      setToastNotification(prev => (prev?.id === notif.id || prev?._id === notif._id ? null : prev));
    }, 8000);
  }, []);

  // Add incoming notification to state, storage & screen toast
  const handleIncomingNotification = useCallback((notifData) => {
    const notif = {
      id: notifData.id || notifData._id || Date.now().toString(),
      title: notifData.title || 'Notification',
      message: notifData.message || notifData.body || 'You have a new update',
      type: notifData.type || notifData.data?.type || 'general',
      data: notifData.data || {},
      read: false,
      timestamp: notifData.timestamp || notifData.createdAt || new Date().toISOString()
    };

    setNotifications(prev => {
      const exists = prev.some(n => (n.id === notif.id || n._id === notif.id));
      if (exists) return prev;
      const updated = [notif, ...prev];
      saveNotificationsToStorage(updated);
      return updated;
    });

    setUnreadCount(prev => prev + 1);
    showToast(notif);
  }, [showToast]);

  // Fetch notifications from backend REST API
  const fetchNotifications = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    setLoading(true);
    try {
      const res = await api.get('/user/notifications');
      const data = res.data;
      const notifList = data?.notifications || data?.data || (Array.isArray(data) ? data : []);
      const readIds = getReadIdsFromStorage();

      if (notifList.length > 0) {
        // Apply local read status
        const normalizedList = notifList.map(n => {
          const nid = n.id || n._id;
          if (readIds.has(nid)) {
            return { ...n, read: true };
          }
          return n;
        });

        setNotifications(prev => {
          const prevIds = new Set(prev.map(p => p.id || p._id));
          const unreadNew = normalizedList.filter(n => !n.read && !prevIds.has(n.id || n._id));

          // Auto trigger toast popup for the newest unread notification
          if (unreadNew.length > 0) {
            showToast(unreadNew[0]);
          }

          const merged = [...normalizedList];
          prev.forEach(p => {
            const pid = p.id || p._id;
            if (!merged.some(m => ((m.id || m._id) === pid))) {
              merged.push(p);
            }
          });
          merged.sort((a, b) => new Date(b.timestamp || b.createdAt || 0) - new Date(a.timestamp || a.createdAt || 0));
          saveNotificationsToStorage(merged);
          return merged;
        });

        const unread = normalizedList.filter(n => !n.read && !readIds.has(n.id || n._id)).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.warn('REST Notification Fetch Note:', err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    try {
      const readIds = getReadIdsFromStorage();
      if (notificationId) readIds.add(notificationId.toString());
      saveReadIdsToStorage(readIds);

      setNotifications(prev => {
        const updated = prev.map(n => ((n.id === notificationId || n._id === notificationId) ? { ...n, read: true } : n));
        saveNotificationsToStorage(updated);
        return updated;
      });
      setUnreadCount(prev => Math.max(0, prev - 1));

      const token = getAuthToken();
      if (token) {
        // Send correct backend payload
        await api.put('/user/notifications/read', { ids: [notificationId] }).catch(() => {});
      }
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const readIds = getReadIdsFromStorage();
      notifications.forEach(n => {
        const nid = n.id || n._id;
        if (nid) readIds.add(nid.toString());
      });
      saveReadIdsToStorage(readIds);

      setNotifications(prev => {
        const updated = prev.map(n => ({ ...n, read: true }));
        saveNotificationsToStorage(updated);
        return updated;
      });
      setUnreadCount(0);

      const token = getAuthToken();
      if (token) {
        // Send empty body to backend to mark ALL notifications as read
        await api.put('/user/notifications/read', {}).catch(() => {});
      }
    } catch (err) {
      console.error('Error marking all read:', err);
    }
  };

  // Helper for manual demo notification testing
  const triggerDemoNotification = (title, message, type = 'general') => {
    handleIncomingNotification({
      id: Date.now().toString(),
      title: title || 'Real-time update',
      message: message || 'Your notification details are now active.',
      type,
      timestamp: new Date().toISOString()
    });
  };

  // Toast Banner click handler
  const handleToastClick = (notif) => {
    if (!notif) return;
    markAsRead(notif.id || notif._id);
    setToastNotification(null);

    const type = (notif.type || notif.data?.type || '').toLowerCase();
    const orderId = notif.data?.orderId || notif.orderId;

    if (type.includes('refund') || type.includes('wallet')) {
      window.location.href = '/refunds';
    } else if (orderId) {
      window.location.href = `/order-details/${orderId}`;
    } else if (type.includes('order')) {
      window.location.href = '/order-tracking';
    }
  };

  const [authTokenState, setAuthTokenState] = useState(() => getAuthToken());

  // Listen to auth changes
  useEffect(() => {
    const handleAuthChange = () => {
      const currentToken = getAuthToken();
      setAuthTokenState(currentToken);
      if (currentToken) {
        fetchNotifications();
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
    };

    window.addEventListener('storage', handleAuthChange);
    window.addEventListener('userLoggedIn', handleAuthChange);
    window.addEventListener('tokenReady', handleAuthChange);
    window.addEventListener('userLoggedOut', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('userLoggedIn', handleAuthChange);
      window.removeEventListener('tokenReady', handleAuthChange);
      window.removeEventListener('userLoggedOut', handleAuthChange);
    };
  }, [fetchNotifications]);

  // Initialize Socket connection and REST polling
  useEffect(() => {
    const token = authTokenState || getAuthToken();
    const userId = getUserId();
    if (!token) return;

    const socketUrl = process.env.REACT_APP_SOCKET_URL ||
      (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api\/?$/, '').replace(/\/+$/, '');

    const newSocket = io(socketUrl, {
      auth: { token },
      query: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 15,
      reconnectionDelay: 1000,
    });

    setSocket(newSocket);

    newSocket.on('connect', () => {
      if (userId) {
        newSocket.emit('join', userId);
        newSocket.emit('join:user', userId);
        newSocket.emit('join:customer', userId);
      }
    });

    newSocket.on('connect_error', (err) => {
      console.warn('⚠️ User WebApp Socket Connect Error:', err.message);
    });

    // Event Subscriptions
    newSocket.on('notification:new', (data) => handleIncomingNotification(data));

    newSocket.on('order:status', (data) => {
      const orderIdStr = (data.orderId || data.id || '').toString().slice(-6);
      const statusStr = (data.status || 'UPDATED').replace(/_/g, ' ').toUpperCase();
      handleIncomingNotification({
        title: `Order #${orderIdStr} Status: ${statusStr}`,
        message: data.message || `Your order status is now: ${statusStr}`,
        type: 'order_status',
        data: { orderId: data.orderId || data.id, status: data.status }
      });
    });

    newSocket.on('order:accepted', (data) => {
      handleIncomingNotification({
        title: 'Order Accepted 🍽️',
        message: data.message || 'The restaurant has accepted your order and started preparing it!',
        type: 'order_status',
        data: { orderId: data.orderId || data.id }
      });
    });

    newSocket.on('order:rider_assigned', (data) => {
      handleIncomingNotification({
        title: 'Rider Assigned 🏍️',
        message: data.message || 'A delivery rider has been assigned to your order!',
        type: 'order_status',
        data: { orderId: data.orderId || data.id }
      });
    });

    newSocket.on('order:delivered', (data) => {
      handleIncomingNotification({
        title: 'Order Delivered 🎉',
        message: data.message || 'Your order has been delivered successfully. Enjoy your meal!',
        type: 'order_status',
        data: { orderId: data.orderId || data.id }
      });
    });

    newSocket.on('order:cancelled', (data) => {
      handleIncomingNotification({
        title: 'Order Cancelled ❌',
        message: data.message || 'Your order has been cancelled.',
        type: 'order_status',
        data: { orderId: data.orderId || data.id }
      });
    });

    newSocket.on('refund:updated', (data) => {
      handleIncomingNotification({
        title: data.title || 'Refund Request Update 💰',
        message: data.message || `Your refund request status: ${data.status || 'processed'}`,
        type: 'refund',
        data: data.data || {}
      });
    });

    newSocket.on('wallet:updated', (data) => {
      handleIncomingNotification({
        title: 'Wallet Balance Updated 💰',
        message: data.message || 'Your wallet balance has been updated.',
        type: 'refund',
        data: data.data || {}
      });
    });

    // Initial REST Fetch
    fetchNotifications();

    // 10-second polling fallback
    const pollInterval = setInterval(() => {
      fetchNotifications();
    }, 10000);

    return () => {
      clearInterval(pollInterval);
      newSocket.disconnect();
    };
  }, [authTokenState, fetchNotifications, handleIncomingNotification]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      toastNotification,
      dismissToast: () => setToastNotification(null),
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      triggerDemoNotification,
      socket
    }}>
      {children}

      {/* Floating Real-Time Toast Notification Banner Popup Card (Matches Admin Panel style) */}
      {toastNotification && (
        <div
          onClick={() => handleToastClick(toastNotification)}
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            zIndex: 9999999,
            maxWidth: '420px',
            width: '90%',
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '16px 20px',
            boxShadow: '0 20px 45px rgba(0, 0, 0, 0.18)',
            border: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            cursor: 'pointer',
            animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            direction: document.body.dir === 'rtl' ? 'rtl' : 'ltr'
          }}
        >
          <div style={{
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            backgroundColor: '#22c55e',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
          }}>
            ✓
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a', marginBottom: '2px' }}>
              {toastNotification.title}
            </div>
            <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.4' }}>
              {toastNotification.message}
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setToastNotification(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ✕
          </button>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
